<template>
	<div :id="uid" class="mds-tooltip">
		<!-- @slot Слот для контента -->
		<slot />
	</div>
</template>

<style lang="scss" src="./VTooltip.scss" />
<script>
import UniqueIdMixin    from "@/lib/mixins/component-unique-id";
import Constants from "@/core/misc/Constants.js";

export default defineComponent({
	name: "VTooltip",

	mixins: [UniqueIdMixin],

	props: {
		/**
		 * Элемент, около которого будет отрисован попап. Может быть query slector или напрямую html элемент
		 */
		target: {
			type: [String, Object, Element],
		},
		/**
		 * Отступ попапа от целевого элемента. Может быть отрицательным
		 */
		offset: {
			type: Number,
			default: 10,
		},
		/**
		 * Позиция. По умолчанию попап отрисовывается снизу. Возможные значения `top, bottom`
		 */
		position: {
			type: String,
			default: "bottom",
			validate(value) {
				return ["top", "bottom"].includes(value);
			},
		},
		/**
		 * Горизонтальное выравнивание
		 */
		align: {
			type: String,
			default: "auto",
			validate(value) {
				return Constants.POPUP_ALIGNS.includes(value);
			},
		},
	},

	data() {
		return {
			visibleLocal: false,
		};
	},

	computed: {},

	watch: {
		target(newVal) {
			if (
				this.$core.utils.isObject(newVal) &&
				this.$core.utils.isFunction(newVal.addEventListener)
			) {
				newVal.addEventListener("mouseenter", this.show);
				newVal.addEventListener("mouseleave", this.hide);
			}
			this.calculatePosition();
		},
	},

	mounted() {
		let wrapper = this.getTooltipWrapper();
		if (!wrapper) {
			wrapper = document.createElement("div");
			wrapper.id = "tooltip-wrapper";
			wrapper.name = "tooltip-wrapper";
			document.body.appendChild(wrapper);
		}
		wrapper.appendChild(this.$el);

		window.addEventListener("scroll", this.calculatePosition, {
			capture: true,
			passive: true
		});

		const targetEl = this.getTarget();
		if (
			this.$core.utils.isObject(targetEl) &&
			this.$core.utils.isFunction(targetEl.addEventListener)
		) {
			targetEl.addEventListener("mouseenter", this.show);
			targetEl.addEventListener("mouseleave", this.hide);
		}
	},

	beforeUnmount() {
		window.removeEventListener("scroll", this.calculatePosition, {
			capture: true,
			passive: true
		});
		const wrapper = this.getTooltipWrapper();
		const popup = this.getTooltip();
		if (popup) {
			wrapper.removeChild(popup);
		}
		if (wrapper.childElementCount === 0) {
			document.body.removeChild(wrapper);
		}
	},

	methods: {
		show() {
			this.visibleLocal = true;
			const tooltip = this.getTooltip();
			tooltip.style.display = "block";
			this.calculatePosition();
			tooltip.classList.add("visible");
		},
		hide() {
			const tooltip = this.getTooltip();
			tooltip.classList.remove("visible");
			setTimeout(() => (tooltip.style = ""), 300);
			this.visibleLocal = false;
		},
		getTooltipWrapper() {
			return document.body.children.namedItem("tooltip-wrapper");
		},
		getTarget() {
			if (typeof this.target === "string") {
				return this.$parent.$el.querySelector(this.target);
			} else if (this.target instanceof Element) {
				return this.target;
			} else if (
				this.target instanceof Object &&
				this.target.hasOwnProperty("$el")
			) {
				return this.target.$el;
			}
			return null;
		},
		getTooltip() {
			const wrapper = this.getTooltipWrapper();
			return wrapper ? wrapper.querySelector("#" + this.uid) : null;
		},
		getDocumentSize() {
			const html = document.documentElement;

			return {
				width: html.clientWidth,
				height: html.clientHeight,
			};
		},
		setStyles(target, styles) {
			if (target && target instanceof HTMLElement && styles instanceof Object) {
				Object.keys(styles).map((key) => {
					target.style[key] = styles[key];
				});
			}
		},
		calculatePosition() {
			const target = this.getTarget();
			if (!target || !this.visibleLocal) return;

			const targetPosition = {
				top: target.getBoundingClientRect().top,
				left: target.getBoundingClientRect().left,
			};
			const targetSize = {
				width: target.offsetWidth,
				height: target.offsetHeight,
			};

			const tooltip = this.getTooltip();
			const tooltipStyles = {};
			const tooltipSize = {
				width: tooltip.offsetWidth,
				height: tooltip.offsetHeight,
			};
			const tooltipPosition = {
				top: 0,
				left: 0,
			};

			let position = this.position;
			if (this.align === "auto") {
				position += "-middle";
			} else {
				position += `-${this.align}`;
			}

			// Считаем координаты попапа в зависимости от получившейся позиции
			switch (position) {
				case "top-middle":
					tooltipPosition.top =
						targetPosition.top - tooltipSize.height - this.offset;
					tooltipPosition.left =
						targetPosition.left + targetSize.width / 2 - tooltipSize.width / 2;
					break;
				case "top-left":
					tooltipPosition.top =
						targetPosition.top - tooltipSize.height - this.offset;
					tooltipPosition.left = targetPosition.left;
					break;
				case "top-right":
					tooltipPosition.top =
						targetPosition.top - tooltipSize.height - this.offset;
					tooltipPosition.left =
						targetPosition.left + targetSize.width - tooltipSize.width;
					break;

				case "bottom-middle":
					tooltipPosition.top =
						targetPosition.top + targetSize.height + this.offset;
					tooltipPosition.left =
						targetPosition.left + targetSize.width / 2 - tooltipSize.width / 2;
					break;
				case "bottom-left":
					tooltipPosition.top =
						targetPosition.top + targetSize.height + this.offset;
					tooltipPosition.left = targetPosition.left;
					break;
				case "bottom-right":
					tooltipPosition.top =
						targetPosition.top + targetSize.height + this.offset;
					tooltipPosition.left =
						targetPosition.left + targetSize.width - tooltipSize.width;
					break;
			}

			// Подготавливаем и устанавливаем позицию попапа
			Object.keys(tooltipPosition).map((key) => {
				if (tooltipPosition[key] !== 0) {
					tooltipStyles[key] = `${tooltipPosition[key]}px`;
				}
			});

			this.setStyles(tooltip, tooltipStyles);
		},
	},
});
</script>
